'use client'
/**
 * lib/supabase/client.ts
 *
 * Cliente Supabase para Client Components, com camada `auth` substituida
 * pela sessao Zitadel via /api/auth/me.
 *
 * Espelha a estrategia usada em lib/supabase/server.ts (cutover Server 2):
 *   - auth.getUser() / getSession() le da sessao Zitadel httpOnly cookie
 *   - from() / rpc() / storage proxy ao client Supabase real (anon key)
 *   - signOut() redireciona /api/auth/logout (SLO Zitadel)
 *
 * Substitui o uso direto de `createBrowserClient` de `@supabase/ssr`
 * para que codigo legado que faz `supabase.auth.getSession()` funcione
 * apos a migracao Zitadel sem refactor.
 *
 * Slug: f7-cutover-server2-zitadel-client-shim
 */

import { createBrowserClient } from '@supabase/ssr'

interface AuthLikeUser {
  id: string
  email?: string | null
  user_metadata?: Record<string, unknown>
  app_metadata?: Record<string, unknown>
  aud?: string
  role?: string
}

interface AuthLikeSession {
  access_token: string
  token_type: 'bearer'
  user: AuthLikeUser
  expires_at?: number
  expires_in?: number
}

interface MeResponse {
  user: {
    id: string | null
    zitadelId: string
    email: string | null
    name: string | null
    isAdmin: boolean
    avatarUrl: string | null
    credits: number
  } | null
}

let cachedMe: MeResponse | null = null
let inflight: Promise<MeResponse> | null = null

async function fetchMe(): Promise<MeResponse> {
  if (cachedMe) return cachedMe
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const res = await fetch('/api/auth/me', {
        credentials: 'include',
        cache: 'no-store',
      })
      if (!res.ok) {
        cachedMe = { user: null }
      } else {
        cachedMe = (await res.json()) as MeResponse
      }
      return cachedMe
    } catch {
      cachedMe = { user: null }
      return cachedMe
    } finally {
      inflight = null
    }
  })()
  return inflight
}

function meToAuthLikeUser(me: MeResponse): AuthLikeUser | null {
  if (!me.user) return null
  return {
    id: me.user.id ?? me.user.zitadelId,
    email: me.user.email,
    user_metadata: {
      full_name: me.user.name,
      name: me.user.name,
      avatar_url: me.user.avatarUrl,
    },
    app_metadata: { provider: 'zitadel', providers: ['zitadel'] },
    aud: 'authenticated',
    role: 'authenticated',
  }
}

function makeAuthProxy() {
  return {
    async getUser() {
      const me = await fetchMe()
      const user = meToAuthLikeUser(me)
      return { data: { user }, error: null as null }
    },
    async getSession() {
      const me = await fetchMe()
      const user = meToAuthLikeUser(me)
      if (!user) {
        return { data: { session: null as AuthLikeSession | null }, error: null as null }
      }
      // Sessao formal: Zitadel id_token como bearer (validado server-side
      // pelos endpoints internos via JWKS). expires_at desconhecido aqui;
      // o /api/auth/me poderia retornar isso se necessario.
      const session: AuthLikeSession = {
        access_token: 'zitadel-cookie-session',
        token_type: 'bearer',
        user,
        expires_at: undefined,
        expires_in: undefined,
      }
      return { data: { session }, error: null as null }
    },
    async refreshSession() {
      // Forca refetch (mata cache local). Server side faz refresh real.
      cachedMe = null
      return this.getSession()
    },
    onAuthStateChange(_callback: (event: string, session: AuthLikeSession | null) => void) {
      // No-op stub: codigo legado costuma usar isso pra rerender.
      // Como a sessao Zitadel e httpOnly + server-side, mudancas vem por refresh
      // de pagina ou navegacao. Retornamos um unsubscriber compativel.
      return {
        data: {
          subscription: {
            unsubscribe: () => {
              /* no-op */
            },
          },
        },
      }
    },
    async signOut() {
      cachedMe = null
      if (typeof window !== 'undefined') {
        window.location.href = '/api/auth/logout'
      }
      return { error: null as null }
    },
    async signInWithPassword() {
      return {
        data: { user: null, session: null },
        error: {
          name: 'AuthMigrated',
          message: 'Login local desabilitado. Use /api/auth/login (Zitadel).',
          status: 410,
        },
      }
    },
    async signInWithOAuth(opts?: { provider?: string }) {
      const idp = opts?.provider ?? 'google'
      if (typeof window !== 'undefined') {
        window.location.href = `/api/auth/login?idp=${idp}`
      }
      return { data: { url: `/api/auth/login?idp=${idp}`, provider: idp }, error: null }
    },
  }
}

/**
 * Cliente browser "Supabase-compativel" com auth via Zitadel.
 *
 * Uso:
 *   const supabase = createClient()
 *   const { data: { session } } = await supabase.auth.getSession()
 *   const { data } = await supabase.from('profiles').select('*')
 */
export function createClient() {
  const real = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
  )
  const auth = makeAuthProxy()
  return new Proxy(real, {
    get(target, prop, receiver) {
      if (prop === 'auth') return auth
      const value = Reflect.get(target, prop, receiver)
      if (typeof value === 'function') {
        return (value as (...args: unknown[]) => unknown).bind(target)
      }
      return value
    },
  }) as unknown as ReturnType<typeof createBrowserClient> & { auth: ReturnType<typeof makeAuthProxy> }
}
